import {  ORDER_STATUS } from "../assets/constants";
import { Order } from "../types/order-interfaces";

import { makeMPServerRequest } from "../assets/helpers";

/**
 * Determines the note status based on the order status from a list of orders.
 * The function checks each order's status and returns a corresponding note status:
 * - "in progress" if the order status is in progress
 * - "done" if the order status is complete
 * - "requested" if the order status is requested
 * - "error" if the order status is error
 *
 * The function first prioritizes non-error orders. If no non-error order is found, 
 * it then considers errored orders.
 * 
 * @param {Order[]} orders - An array of orders where each order contains a status.
 * @returns {string} - A string representing the note status of the order:
 *   - "in progress" for in-progress orders
 *   - "done" for completed orders
 *   - "requested" for requested orders
 *   - "error" for orders in an error state
 */
export function getNoteStatusFromOrderStatus(orders: Order[]) {
  const nonErrorOrder = orders.find(
    (order: Order) => !ORDER_STATUS.ERROR.includes(order.orderStatus)
  );
  const errorOrder = orders.find((order: Order) =>
    ORDER_STATUS.ERROR.includes(order.orderStatus)
  );
  // Match any order without error before considering any errored out orders
  const matchingOrder = nonErrorOrder ? nonErrorOrder : errorOrder;
  const orderStatus = matchingOrder ? matchingOrder.orderStatus : '';
  let status = "no-value";
  if (ORDER_STATUS.IN_PROGRESS.includes(orderStatus)) {
    status = "in-progress";
  } else if (ORDER_STATUS.COMPLETE.includes(orderStatus)) {
    status = "done";
  } else if (ORDER_STATUS.REQUESTED.includes(orderStatus)) {
    status = "requested";
  } else if (ORDER_STATUS.ERROR.includes(orderStatus)) {
    status = "error";
  }
  return {
    noteStatus: status,
    orderStatus,
    matchingOrder,
  }
}


/**
 * Retrieves a list of orders from the server.
 *
 * This function makes a GET request to the server endpoint to fetch a list of orders. 
 * If the request is successful, it returns the orders data as an array of `Order` objects.
 * In case of an unexpected response or failure, it throws an error with details about the issue.
 *
 * @async
 * @function getOrders
 * @returns {Promise<Order[]>} A promise that resolves to an array of `Order` objects.
 * @throws {Error} Throws an error if the server response is unexpected or if an exception occurs during the request.
 */
export async function getOrders(): Promise<Order[]> {
  try {
    const mpResponse = await makeMPServerRequest(
      "self/orders/",
      "GET",
      "json",
    );

    if (mpResponse.status === "success") {
      return mpResponse.data as Order[];
    } else {
      throw new Error(
        `Unexpected response while getting order list: ${JSON.stringify(mpResponse)}`,
      );
    }
  } catch (error) {
    throw new Error(
      `Unexpected exception while getting order list: ${error}`
    );
  }
}


